import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { createJournalEntry, getChartOfAccounts, getBranches } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

function money(n) {
  return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const EMPTY_LINE = { account_id: "", debit: "", credit: "", description: "" };

export default function AddJournalEntry() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("accounting");

  const [accounts, setAccounts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState("");
  const [branchId, setBranchId] = useState("");
  const [lines, setLines] = useState([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getChartOfAccounts(token).then(setAccounts).catch(() => notify.error(t("notify_load_accounts_failed", "Failed to load accounts")));
    getBranches(token).then(setBranches).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const accountOptions = accounts.map((a) => ({ value: String(a.id), label: `${a.code} — ${a.name}` }));
  const branchOptions = [{ value: "", label: t("field_branch_none_option", "No specific branch") }, ...branches.map((b) => ({ value: String(b.id), label: b.name }))];

  const totals = useMemo(() => {
    let debit = 0, credit = 0;
    lines.forEach((l) => {
      debit += Number(l.debit) || 0;
      credit += Number(l.credit) || 0;
    });
    return { debit, credit };
  }, [lines]);

  const isBalanced = lines.length >= 2 && totals.debit > 0 && Math.abs(totals.debit - totals.credit) < 0.01;

  const updateLine = (idx, field, value) => {
    setLines((prev) => {
      const next = [...prev];
      const line = { ...next[idx], [field]: value };
      // A line is Debit XOR Credit — filling one clears the other, so a
      // cashier-style single-amount entry per line stays impossible to
      // mess up rather than needing its own validation message.
      if (field === "debit" && value) line.credit = "";
      if (field === "credit" && value) line.debit = "";
      next[idx] = line;
      return next;
    });
  };

  const addLine = () => setLines((prev) => [...prev, { ...EMPTY_LINE }]);
  const removeLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!description.trim()) newErrors.description = t("validation_description_required", "Description is required");
    lines.forEach((l, i) => {
      if (!l.account_id) newErrors[`line_${i}`] = t("validation_line_account_required", "Every line needs an account");
    });
    if (!isBalanced) newErrors.balance = t("validation_not_balanced", "Total debit must equal total credit before this can be saved");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    setLoading(true);
    const payload = {
      entry_date: entryDate,
      description: description.trim(),
      branch_id: branchId ? Number(branchId) : 0,
      lines: lines
        .filter((l) => l.account_id && (Number(l.debit) > 0 || Number(l.credit) > 0))
        .map((l) => ({
          account_id: Number(l.account_id),
          debit: Number(l.debit) || 0,
          credit: Number(l.credit) || 0,
          description: l.description.trim(),
        })),
    };

    try {
      await createJournalEntry(token, payload);
      notify.success(t("notify_journal_entry_created", "Journal entry posted successfully"));
      navigate("/journal-entries");
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/journal-entries")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{t("add_journal_entry_title", "New Journal Entry")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("add_journal_entry_subtitle", "A manual entry — every line's debit and credit together must balance to zero")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FloatingInput label={t("field_entry_date_label", "Date")} type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} />
            <SelectBox label={t("field_branch_label", "Branch (optional)")} options={branchOptions} value={branchId} onChange={setBranchId} />
          </div>

          <FloatingInput label={t("field_entry_description_label", "Description")} value={description} onChange={(e) => setDescription(e.target.value)} error={errors.description} />

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold uppercase text-gray-500">{t("lines_section_title", "Lines")}</h3>
              <Button variant="outline" size="sm" icon={ICONS.Plus} onClick={addLine}>{t("btn_add_line", "Add Line")}</Button>
            </div>

            <div className="space-y-3">
              {lines.map((line, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_2fr_auto] gap-2 items-start p-3 rounded-2xl bg-gray-50/50 border border-gray-100">
                  <SelectBox
                    label={t("field_account_label", "Account")}
                    options={accountOptions}
                    value={line.account_id}
                    onChange={(val) => updateLine(idx, "account_id", val)}
                    error={errors[`line_${idx}`]}
                  />
                  <FloatingInput label={t("field_debit_label", "Debit")} type="number" value={line.debit} onChange={(e) => updateLine(idx, "debit", e.target.value)} />
                  <FloatingInput label={t("field_credit_label", "Credit")} type="number" value={line.credit} onChange={(e) => updateLine(idx, "credit", e.target.value)} />
                  <FloatingInput label={t("field_line_description_label", "Line note (optional)")} value={line.description} onChange={(e) => updateLine(idx, "description", e.target.value)} />
                  <button
                    type="button"
                    onClick={() => removeLine(idx)}
                    disabled={lines.length <= 2}
                    className="h-14 w-14 flex items-center justify-center rounded-xl text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                  >
                    <ICONS.DeleteUser className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className={`p-4 rounded-2xl flex items-center justify-between font-mono font-bold ${isBalanced ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
            <span>{t("totals_debit_label", "Total Debit")}: {money(totals.debit)}</span>
            <span>{t("totals_credit_label", "Total Credit")}: {money(totals.credit)}</span>
            <span>{isBalanced ? t("totals_balanced", "Balanced ✓") : t("totals_unbalanced", "Not balanced")}</span>
          </div>
          {errors.balance && <p className="text-[11px] text-red-500 font-bold uppercase tracking-tight">{errors.balance}</p>}

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              disabled={!isBalanced}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={ICONS.Save}
            >
              {t("btn_post_entry", "Post Journal Entry")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/journal-entries")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
